import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import {
  getCollectionProps,
  getFormProps,
  getSelectProps,
  useForm,
  useInputControl,
} from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod/v4';

import { DropdownField } from '@shieldpay/bumblebee/dropdown-field';
import { DropdownSearch } from '@shieldpay/bumblebee/dropdown-search';
import { DropdownSearchField } from '@shieldpay/bumblebee/dropdown-search-field';
import { DropdownSingle } from '@shieldpay/bumblebee/dropdown-single';
import { Form } from '@shieldpay/bumblebee/form';
import { Heading } from '@shieldpay/bumblebee/heading';
import DropdownMarkdown from '@shieldpay/bumblebee/markdown/dropdown.md';
import { MultipleDropdownSearchField } from '@shieldpay/bumblebee/multiple-dropdown-search-field';
import { Text } from '@shieldpay/bumblebee/text';

import { noop } from '@shieldpay/utility-functions-ui/noop';

import { ContentsTable } from '~/components/contents-table/contents-table';
import { Design } from '~/components/design/design';
import { MdxFormatter } from '~/components/mdx-formatter/mdx-formatter';
import { ComponentPreview } from '~/components/preview/component-preview';
import { createMeta } from '~/utilities/create-meta';

export const meta = createMeta({
  id: 'hive.components.dropdown.title',
});

const exampleItems = [
  { label: 'Bank of Mum‘n’Dad', value: '11' },
  { label: 'Unavailable bank', value: 'xxx', disabled: true },
  { label: 'Fine Ants', value: '22' },
  {
    label: 'The Royal Bank of Haywards Heath',
    value: '33',
  },
  { label: 'WTF Bank', value: '44' },
  { label: 'Shieldbank', value: '55' },
  { label: 'Paybank', value: '66' },
  { label: 'Bank of Pay', value: '77' },
  { label: 'Bank of Shield', value: '88' },
  { label: 'Bank of Single dads', value: 'a11' },
  { label: 'Fine Schwine', value: 'a22' },
  {
    label: 'The Royal Bank of HH',
    value: 'a33',
  },
  { label: 'WTAF Bank', value: 'a44' },
  { label: 'BankShield', value: 'a55' },
  { label: 'BankPay', value: 'a66' },
  { label: 'Pay Society', value: 'a77' },
  { label: 'Shield Society', value: 'a88' },
  { label: 'Bank of Mum‘n’Dad', value: 'b11' },
  { label: 'Fine Antz', value: 'b22' },
  {
    label: 'The Royal Bank of Haywards Heath',
    value: 'b33',
  },
  { label: 'WTF Bank', value: 'b44' },
  { label: 'Shieldbank', value: 'b55' },
  { label: 'Paybank', value: 'b66' },
  { label: 'Bank of Pay', value: 'b77' },
  { label: 'Bank of Shield', value: 'b88' },
  { label: 'Bank of Single dads', value: 'ca11' },
  { label: 'Fine Schwine', value: 'ca22' },
  {
    label: 'The Royal Bank of HH',
    value: 'ca33',
  },
  { label: 'WTAF Bank', value: 'ca44' },
  { label: 'BankShield', value: 'ca55' },
  { label: 'BankPay', value: 'ca66' },
  { label: 'Pay Society', value: 'ca77' },
  { label: 'Shield Society', value: 'ca88' },
];

const FieldSchema = z.object({
  single: z.string(),
  initialError: z.string().refine((value) => value !== '22', {
    message: 'Can‘t choose that!',
  }),
  search: z.string(),
  multipleSearch: z.array(z.string()),
});

export const DropdownComponentPage = () => {
  const [controlledValue1, setControlledValue1] = useState(
    exampleItems[3]?.value,
  );

  const [form, fields] = useForm({
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate: ({ formData }) =>
      parseWithZod(formData, { schema: FieldSchema }),
  });

  const collectionProps = getCollectionProps(fields.multipleSearch, {
    type: 'checkbox',
    options: exampleItems.map((item) => String(item.value)),
  });

  const errorControl = useInputControl(fields.initialError);

  useEffect(() => {
    // allows us to show an error state initially
    errorControl.blur();
  }, [errorControl]);

  return (
    <>
      <Heading>
        <FormattedMessage id="hive.components.dropdown.title" />
      </Heading>

      <ContentsTable />

      <ComponentPreview>
        <Form {...getFormProps(form)} spacing="basePos8">
          <Heading>DropdownField (for use in forms)</Heading>
          <DropdownField
            {...getSelectProps(fields.single)}
            errors={fields.single.errors}
            label={<>DropdownField using Conform</>}
            placeholder="Select account…"
            items={exampleItems}
          />

          <DropdownField
            {...getSelectProps(fields.initialError)}
            errors={fields.initialError.errors}
            defaultValue={exampleItems[1]?.value}
            size="large"
            label={<>Large DropdownField with validation error</>}
            helpText="Selecting “Fine Ants” will trigger the error"
            placeholder="Select account…"
            items={exampleItems}
          />

          <DropdownField
            id="disabledDropdown"
            name="disabledDropdown"
            label={<>disabled DropdownField</>}
            placeholder="Select account…"
            disabled
            items={exampleItems}
          />
          <DropdownField
            id="readonlyDropdown"
            name="readonly"
            label={<>readonly DropdownField</>}
            placeholder="Select account…"
            readOnly
            defaultValue="11"
            items={exampleItems}
          />

          <Heading>DropdownSearchField (for use in forms)</Heading>

          <DropdownSearchField
            {...getSelectProps(fields.search)}
            errors={fields.search.errors}
            label={<>DropdownSearchField using Conform</>}
            placeholder="Select account…"
            items={exampleItems}
            noResultsLabel="no results"
          />

          <DropdownSearchField
            id="disabledDropdown"
            name="disabledDropdown"
            label={<>disabled DropdownSearchField</>}
            placeholder="Select account…"
            disabled
            items={exampleItems}
            noResultsLabel="no results"
          />
          <DropdownSearchField
            id="readonlyDropdown"
            name="readonly"
            label={<>readonly DropdownSearchField</>}
            placeholder="Select account…"
            defaultValue={exampleItems[2]?.value}
            readOnly
            items={exampleItems}
            noResultsLabel="no results"
          />

          <Heading>MultipleDropdownSearchField (for use in forms)</Heading>

          <MultipleDropdownSearchField
            errors={fields.search.errors}
            label={<>MultipleDropdownSearchField using Conform</>}
            placeholder="Select banks…"
            selectedText={(count: number) =>
              count === 1 ? `${count} item selected` : `${count} items selected`
            }
            items={exampleItems}
            defaultValues={['a44', 'a55']}
            noResultsLabel="no results"
            collection={collectionProps}
          />
        </Form>

        <Heading>DropdownSingle (for use in UI, not forms)</Heading>
        <DropdownSingle
          label={<>Regular size Dropdowns</>}
          placeholder="Select account…"
          items={exampleItems}
          onChange={noop}
        />
        <DropdownSingle
          label={<>Large Dropdown</>}
          size="large"
          placeholder="Select account…"
          items={exampleItems}
          onChange={noop}
        />
        <DropdownSingle
          label={<>Disabled Dropdown</>}
          placeholder="Select account…"
          disabled
          items={exampleItems}
          onChange={noop}
        />
        <DropdownSingle
          label={<>Controlled Dropdown with selected value</>}
          placeholder="Select account…"
          items={exampleItems}
          value={controlledValue1}
          onChange={setControlledValue1}
        />

        <Heading>Dropdown Search (for use in UI, not forms)</Heading>
        <DropdownSearch
          label={<>Regular size Dropdowns</>}
          placeholder="Select account…"
          items={exampleItems}
          onChange={noop}
          noResultsLabel="no results"
        />
      </ComponentPreview>

      <Design>
        <Design.Section heading="Usage">
          <Text paragraph>
            The dropdown component will be when the user has multiple options to
            choose from. It can be used within a form to alter the content of a
            page - the type of dropdown will depend on the usage.
          </Text>
          <Text paragraph>
            This is to be used on a form when the user is or to alter the
            content of a page
          </Text>
        </Design.Section>
        <Design.Section heading="Specs">
          <Text paragraph>
            A dropdown will consist of a background plate, a chevron icon, an
            open panel list with options and (depending on its type) a text
            label.
          </Text>
          <Heading level="4" variant="title">
            Large dropdown
          </Heading>
          <Text paragraph>
            This is a regular full-sized dropdown to be used along with text
            inputs in forms and against a large button component. It will have
            the same attributes as a text input including a text label and error
            functionality.
          </Text>
          <Heading level="4" variant="title">
            Medium dropdown
          </Heading>
          <Text paragraph>
            This is a slightly shorter dropdown to be used outside of the
            context of a form when there is less space on the page (i.e.
            selecting a marketplace on the Balances page) and against a small
            button component.
          </Text>
          <Text paragraph>
            This is a slightly shorter dropdown to be used outside of the
            context of a form when there is less space on the page (i.e.
            selecting a marketplace on the Balances page) and against a small
            button component.
          </Text>
        </Design.Section>
        <Text paragraph>Please refer to Specs for examples</Text>
        <Design.Section heading="Behaviour">
          <Text paragraph>
            The dropdown component can be opened by the user in multiple ways.
          </Text>
          <Text paragraph>
            The user can hover upon and click on the dropdown to open it and
            then hover over and select the options. The user can also use the
            keyboard button to interact.
          </Text>
          <Text paragraph>
            When the dropdown is in the Active state, the user should be able to
            hover over other items while it is Active.
          </Text>
          <Text paragraph>
            Selecting any option on the dropdown list should change the dropdown
            state from Active back to Focus and populate the field with the
            selected option. Clicking anywhere outside the dropdown list should
            change the dropdown state to Default.
          </Text>
          <Text paragraph>
            Transition to display a state change with a 0.25 ease.
          </Text>
          <Text paragraph>
            Animations In (dropdown opening) to be a Scale at Cubic Ease-out at
            120ms
          </Text>
          <Text paragraph>
            Animations Out (dropdown closing) should not have any motion.
          </Text>

          <Heading level="4" variant="title">
            Reverse active state
          </Heading>
          <Text paragraph>
            In the case where there is no space below a dropdown on the viewport
            (i.e. the placement is at the bottom of the screen or the user has
            scrolled) the Options list should open upwards instead of downwards.
          </Text>

          <Heading level="4" variant="title">
            Scrollable option list
          </Heading>
          <Text paragraph>
            The scrollable Options list should display a maximum of 6 options,
            after which it should scroll.
          </Text>
          <Text paragraph>
            The user should be able to use the mouse, the keyboard (once active)
            or click and drag the scrollbar.
          </Text>
          <Text paragraph>
            Accessible behaviour for scrollable Options lists should be
            consistent i.e the focus state should be shown if they press ENTER
            on the field to it after which the user should be able to use Tab or
            Up and Down arrows to select an option. The Options list should
            always only display 6 options.
          </Text>
          <Text paragraph>
            The scrollbar should resize automatically depending on the amount of
            options in the Option list.
          </Text>
          <Text paragraph>
            Please refer to Behaviour and Prototype for examples
          </Text>
        </Design.Section>
        <Design.Section heading="Placement">
          <Text paragraph>No standard placement for the dropdown.</Text>
          <Text paragraph>
            Please refer to Placement in page designs for examples
          </Text>
        </Design.Section>
        <Design.Section heading="Accessibility">
          <Text paragraph>
            Use the appropriate ARIA attributes for screen readers where
            applicable.
          </Text>
        </Design.Section>
      </Design>

      <MdxFormatter md={DropdownMarkdown} />
    </>
  );
};

export default DropdownComponentPage;
